import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebhooksMonitor() {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [unprocessedCount, setUnprocessedCount] = useState(0);
  const { getToken } = useAdminAuth();

  useEffect(() => {
    fetchLogs();
  }, [statusFilter]);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/webhooks/stripe/logs?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      setLogs(response.data.logs);
      setUnprocessedCount(response.data.unprocessed_count);
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getEventTypeBadge = (type) => {
    const styles = {
      'checkout.session.completed': 'bg-green-500/10 text-green-400 border-green-500/30',
      'invoice.payment_failed': 'bg-red-500/10 text-red-400 border-red-500/30',
      'customer.subscription.updated': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    };
    return styles[type] || 'bg-gray-500/10 text-gray-400 border-gray-500/30';
  };

  const getStatusBadge = (processed) => {
    return processed
      ? 'bg-green-500/10 text-green-400 border-green-500/30'
      : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Monitor de Webhooks Stripe</h1>
          <p className="text-gray-400 mt-1">Eventos recibidos desde Stripe</p>
        </div>
        
        {unprocessedCount > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2">
            <p className="text-yellow-400 font-semibold">
              ⚠️ {unprocessedCount} evento{unprocessedCount !== 1 ? 's' : ''} sin procesar
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-300">Estado:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="">Todos</option>
            <option value="unprocessed">Sin procesar</option>
            <option value="processed">Procesados</option>
          </select>
          
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black font-medium rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] transition-colors"
          >
            Actualizar
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-sm font-medium text-gray-400">No hay webhooks registrados</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Timestamp</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Tipo de Evento</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Session ID</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Payment Status</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1a4d5c]/30">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-[#1a4d5c]/10 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {new Date(log.created_at).toLocaleString('es-ES')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getEventTypeBadge(log.event_type)}`}>
                        {log.event_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-400 font-mono">
                      {log.session_id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {log.payment_status || 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(log.processed)}`}>
                        {log.processed ? 'Procesado' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
